'use client'

import React, {
  createContext,
  Dispatch,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Review } from '@skop-app/lib-domain/types'
import { useParams, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { ReviewPopup } from '@/components/organisms/Reviews/Popup'
import { ReviewGridProjection, ReviewParams, ReviewPopupParams } from '@/types'
import { useGridReviews, useReviews } from '@/services/queries'
import {
  buildReviewGridProjection,
  buildReviewsByCellKey,
  buildReviewsByElementId,
  getAggregateReviewStatusForElement,
  getThreadsForElement,
  hasPendingReviewForCell,
  hasPendingReviewForElement,
  hasPendingReviewForTarget,
  resolveReviewPopupState,
  REVIEW_DRAWER_CLASSNAME,
} from '@/utils/reviews'
import { openDrawer } from '@/components/drawers/drawer.registry.events'

const calculateReviewPosition = (
  params: ReviewPopupParams,
  reviewRef: React.RefObject<HTMLDivElement | null>,
) => {
  const { api, node } = params
  const cell = api?.getFocusedCell()
  const rowTop = node?.rowTop || 0
  const rowHeight = node?.rowHeight || 40
  const verticalRange = api?.getVerticalPixelRange()
  const horizontalRange = api?.getHorizontalPixelRange()

  const reviewRect = reviewRef.current?.getBoundingClientRect()

  const top =
    rowTop +
    rowHeight -
    (verticalRange?.top ?? 0) +
    (reviewRect?.top ?? 0) -
    rowHeight / 2

  const left =
    (cell?.column?.getLeft() ?? 0) +
    (cell?.column?.getActualWidth() ?? 0) -
    (horizontalRange?.left ?? 0) +
    (reviewRect?.left ?? 0) +
    8

  return { top, left }
}

type ReviewDataContextType = {
  reviews?: Review[]
  gridReviews?: Review[]
  allReviews?: Review[]
  reviewGrid: ReviewGridProjection
  reviewStateVersion: string
  reviewsByElementId?: Record<string, Review[]>
  reviewsByCellKey?: Record<string, Review[]>
  reviewsById?: Record<string, Review[]>
  allReviewsByElementId?: Record<string, Review[]>
  findReviewForTarget: (
    params?: ReviewPopupParams['target'],
  ) => Review | undefined
  resolveReviewPopupState: (
    params?: ReviewPopupParams['target'],
  ) => ReturnType<typeof resolveReviewPopupState>
  findThreadByReviewId: (reviewId?: string) => Review | undefined
  getThreadsForElement: (elementID?: string) => Review[]
  hasPendingReviewForCell: (params?: ReviewPopupParams['target']) => boolean
  hasPendingReviewForElement: (elementID?: string) => boolean
  hasPendingReviewForTarget: (params?: ReviewPopupParams['target']) => boolean
  getAggregateReviewStatusForElement: (elementID?: string) => string | undefined
  isLoading: boolean
}

type ReviewPopupContextType = {
  showReviewPopup: (params: ReviewPopupParams) => void
  reviewRef: RefObject<HTMLDivElement | null> // Area where the review can be displayed
}

type ReviewSelectionContextType = {
  setSelectedReviews: Dispatch<SetStateAction<Review[]>>
  selectedReviews?: Review[]
  selectedReviewId?: string
  pendingRevealReviewId?: string
  selectReviewThread: (
    reviewId?: string,
    options?: { revealInDrawer?: boolean },
  ) => void
  clearPendingRevealReviewId: () => void
}

type ReviewUIContextType = ReviewPopupContextType & ReviewSelectionContextType
type ReviewContextType = ReviewDataContextType & ReviewUIContextType

const ReviewDataContext = createContext<ReviewDataContextType | undefined>(
  undefined,
)
const ReviewPopupContext = createContext<ReviewPopupContextType | undefined>(
  undefined,
)
const ReviewSelectionContext = createContext<
  ReviewSelectionContextType | undefined
>(undefined)

type ReviewProviderProps = {
  children: ReactNode
}

export function ReviewProvider({ children }: ReviewProviderProps) {
  const { id: projectId } = useParams<{ id: string | undefined }>()
  const searchParams = useSearchParams()
  const t = useTranslations('Navigation')
  const { data: gridReviews = [], isLoading } = useGridReviews(projectId)
  const { data: allReviews = [] } = useReviews()

  const reviewRef = useRef<HTMLDivElement | null>(null)
  const [selectedReviews, setSelectedReviews] = useState<Review[]>([])
  const [selectedReviewId, setSelectedReviewId] = useState<string | undefined>()
  const [pendingRevealReviewId, setPendingRevealReviewId] = useState<
    string | undefined
  >()
  const [reviewPopupParams, setReviewPopupParams] = useState<ReviewParams>({
    position: {},
    display: 'none',
  })

  const reviews = useMemo(() => gridReviews, [gridReviews])
  const reviewGrid = useMemo(
    () => buildReviewGridProjection(reviews),
    [reviews],
  )
  const allVisibleReviewGrid = useMemo(
    () => buildReviewGridProjection(allReviews),
    [allReviews],
  )
  const reviewsByElementId = useMemo(
    () => reviewGrid.reviewsByElementId,
    [reviewGrid],
  )
  const reviewsByCellKey = useMemo(
    () => reviewGrid.reviewsByCellKey,
    [reviewGrid],
  )
  const allReviewsByElementId = useMemo(
    () => buildReviewsByElementId(allReviews, { visibleOnly: false }),
    [allReviews],
  )
  const allReviewsByCellKey = useMemo(
    () => buildReviewsByCellKey(allReviews, { visibleOnly: false }),
    [allReviews],
  )

  const findThreadByReviewId = useCallback(
    (reviewId?: string) =>
      reviewId
        ? allReviews.find((review) => review.id === reviewId)
        : undefined,
    [allReviews],
  )

  const resolvePopupState = useCallback(
    (target?: ReviewPopupParams['target']) =>
      resolveReviewPopupState({
        target,
        reviewsByCellKey: allReviewsByCellKey,
        reviewsByElementId: allReviewsByElementId,
        visibleReviewsByCellKey: allVisibleReviewGrid.reviewsByCellKey,
        visibleReviewsByElementId: allVisibleReviewGrid.reviewsByElementId,
      }),
    [allReviewsByCellKey, allReviewsByElementId, allVisibleReviewGrid],
  )
  const findReviewForPopupTarget = useCallback(
    (target?: ReviewPopupParams['target']) =>
      resolvePopupState(target).matchedReview,
    [resolvePopupState],
  )

  const hasPendingReviewForPopupTarget = useCallback(
    (target?: ReviewPopupParams['target']) =>
      hasPendingReviewForCell(target, reviewsByCellKey),
    [reviewsByCellKey],
  )

  const hasPendingReviewForCatalogTarget = useCallback(
    (target?: ReviewPopupParams['target']) =>
      hasPendingReviewForTarget({
        target,
        reviewsByCellKey: allReviewsByCellKey,
        reviewsByElementId: allReviewsByElementId,
      }),
    [allReviewsByCellKey, allReviewsByElementId],
  )

  const showReviewPopup = useCallback((params: ReviewPopupParams) => {
    const { clientX = 0, clientY = 0, target } = params

    if (!params.api || !params.node) {
      setReviewPopupParams({
        position: { top: `${clientY - 90}px`, left: `${clientX - 143}px` },
        display: 'flex',
        target,
      })
      return
    }

    const { top, left } = calculateReviewPosition(params, reviewRef)
    setReviewPopupParams({
      position: { top: `${top}px`, left: `${left}px` },
      display: 'flex',
      target,
    })
  }, [])

  const selectReviewThread = useCallback(
    (
      reviewId?: string,
      options?: {
        revealInDrawer?: boolean
      },
    ) => {
      if (!reviewId) {
        setSelectedReviewId(undefined)
        setPendingRevealReviewId(undefined)
        return
      }

      setSelectedReviewId(reviewId)
      setPendingRevealReviewId(options?.revealInDrawer ? reviewId : undefined)
    },
    [],
  )

  const clearPendingRevealReviewId = useCallback(() => {
    setPendingRevealReviewId(undefined)
  }, [])

  const reviewIdFromUrl = searchParams?.get('reviewId') || undefined

  React.useEffect(() => {
    if (!reviewIdFromUrl) return
    openDrawer('reviews', {
      title: t('reviewNotifications.title'),
      className: REVIEW_DRAWER_CLASSNAME,
    })
    if (selectedReviewId !== reviewIdFromUrl) {
      selectReviewThread(reviewIdFromUrl, { revealInDrawer: true })
    }
  }, [reviewIdFromUrl, selectReviewThread, selectedReviewId, t])

  const dataValue = React.useMemo<ReviewDataContextType>(
    () => ({
      gridReviews: reviews,
      allReviews,
      reviewGrid,
      reviewStateVersion: reviewGrid.reviewStateVersion,
      reviews,
      reviewsByElementId,
      reviewsByCellKey,
      reviewsById: reviewsByElementId,
      allReviewsByElementId,
      findReviewForTarget: findReviewForPopupTarget,
      resolveReviewPopupState: resolvePopupState,
      findThreadByReviewId,
      getThreadsForElement: (elementID?: string) =>
        getThreadsForElement(elementID, allReviewsByElementId),
      hasPendingReviewForCell: hasPendingReviewForPopupTarget,
      hasPendingReviewForElement: (elementID?: string) =>
        hasPendingReviewForElement(elementID, reviewsByElementId),
      hasPendingReviewForTarget: hasPendingReviewForCatalogTarget,
      getAggregateReviewStatusForElement: (elementID?: string) =>
        getAggregateReviewStatusForElement(elementID, reviewsByElementId),
      isLoading,
    }),
    [
      allReviews,
      reviewGrid,
      reviews,
      reviewsByElementId,
      reviewsByCellKey,
      allReviewsByElementId,
      findReviewForPopupTarget,
      resolvePopupState,
      findThreadByReviewId,
      hasPendingReviewForPopupTarget,
      hasPendingReviewForCatalogTarget,
      isLoading,
    ],
  )

  const popupValue = React.useMemo<ReviewPopupContextType>(
    () => ({
      reviewRef,
      showReviewPopup,
    }),
    [reviewRef, showReviewPopup],
  )

  const selectionValue = React.useMemo<ReviewSelectionContextType>(
    () => ({
      selectedReviews,
      setSelectedReviews,
      selectedReviewId,
      pendingRevealReviewId,
      selectReviewThread,
      clearPendingRevealReviewId,
    }),
    [
      selectedReviews,
      setSelectedReviews,
      selectedReviewId,
      pendingRevealReviewId,
      selectReviewThread,
      clearPendingRevealReviewId,
    ],
  )

  return (
    <ReviewDataContext.Provider value={dataValue}>
      <ReviewPopupContext.Provider value={popupValue}>
        <ReviewSelectionContext.Provider value={selectionValue}>
          {children}
          <ReviewPopup
            reviewPopupParams={reviewPopupParams}
            setReviewPopupParams={setReviewPopupParams}
          />
        </ReviewSelectionContext.Provider>
      </ReviewPopupContext.Provider>
    </ReviewDataContext.Provider>
  )
}

export const useReviewData = () => {
  const context = useContext(ReviewDataContext)
  if (context === undefined) {
    throw new Error('useReviewData must be used within a ReviewProvider')
  }
  return context
}

export const useReviewPopup = () => {
  const context = useContext(ReviewPopupContext)
  if (context === undefined) {
    throw new Error('useReviewPopup must be used within a ReviewProvider')
  }
  return context
}

export const useReviewSelection = () => {
  const context = useContext(ReviewSelectionContext)
  if (context === undefined) {
    throw new Error('useReviewSelection must be used within a ReviewProvider')
  }
  return context
}

export const useReviewUI = (): ReviewUIContextType => {
  const popup = useReviewPopup()
  const selection = useReviewSelection()
  return React.useMemo(() => ({ ...popup, ...selection }), [popup, selection])
}

export const useReview = (): ReviewContextType => {
  const data = useReviewData()
  const popup = useReviewPopup()
  const selection = useReviewSelection()
  return React.useMemo(
    () => ({ ...data, ...popup, ...selection }),
    [data, popup, selection],
  )
}
